import { ColumnFilterType, CustomFieldLookupService } from "shared/cubiq-services";
import { BfxFindFilterCondition } from "../bfx-find/models/BfxFindFilterCondition";

import { BfxFindDealsFilterFieldName } from "shared/cubiq-services";
import { BfxFindFilterOperatorSet } from "app/bfx-find/models/BfxFindFilterOperatorSet";
import { BfxFindFilterConditionValueParseType } from "app/bfx-find/models/BfxFindFilterConditionValueParseType";
import { FilterData } from "app/bfx-find/bfx-find.constants";
import {
  BfxFindFilterData,
  BfxFindFilterDataFactory,
} from "../bfx-find/bfx-find-filter-data-factory";
import { Injectable } from "@angular/core";
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { TranslateService } from "@ngx-translate/core";

export const ACCOUNT_FILTER_TYPES: Array<ColumnFilterType> = [
  ColumnFilterType.ACCOUNT,
  ColumnFilterType.ACCOUNTID,
];
export const CLIENT_FILTER_TYPES: Array<ColumnFilterType> = [
  ColumnFilterType.CLIENT,
  ColumnFilterType.CLIENTID,
];
export const SINGLE_FILTER_TYPES: Array<ColumnFilterType> =
  ACCOUNT_FILTER_TYPES.concat(CLIENT_FILTER_TYPES);

@Injectable()
export class BfxFindDealsFilterDataFactory extends BfxFindFilterDataFactory {
  private oboEnabled: boolean = false;

  constructor(
    private configService: BfxConfigService,
    customFieldService: CustomFieldLookupService,
    private translate: TranslateService,
  ) {
    super(customFieldService);
    this.initialize();
  }

  private initialize(): void {
    this.configService.getUIConfig().then((config) => {
      this.oboEnabled = config && config.general ? config.general.oboEnabled : false;
    });
  }

  public defineFilterData(
    clientTraderUsersEnabled: boolean,
    fwdEnabled: boolean,
    ndfEnabled: boolean,
    benchmarksEnabled: boolean,
  ): BfxFindFilterData {
    let searchByClient: any = {};
    let searchByType: Partial<FilterData> = {
      id: FilterData.templateId++,
      label: "Search by Type",
    };
    searchByType.filterConditions = this.createOrderTypeFilterConditions(
      benchmarksEnabled,
      fwdEnabled,
      ndfEnabled,
    );

    let searchByDateAndUser = {
      id: FilterData.templateId++,
      label: "Search by Date or User",
      filterConditions: [
        new BfxFindFilterCondition(
          5,
          0,
          BfxFindDealsFilterFieldName.SEARCH_IN_TZ,
          "Search in",
          BfxFindFilterOperatorSet.Equal,
          BfxFindFilterConditionValueParseType.None,
        ),
        new BfxFindFilterCondition(
          5,
          1,
          BfxFindDealsFilterFieldName.ENTRY_DATE,
          "Entry Date",
          BfxFindFilterOperatorSet.All,
          BfxFindFilterConditionValueParseType.DateEquality,
        ),
        new BfxFindFilterCondition(
          5,
          2,
          BfxFindDealsFilterFieldName.ENTRY_USER,
          "Entry User",
          BfxFindFilterOperatorSet.EqualNotEqual,
          BfxFindFilterConditionValueParseType.RefArray,
        ),
        new BfxFindFilterCondition(
          5,
          3,
          BfxFindDealsFilterFieldName.FILL_DATE,
          "Fill Date",
          BfxFindFilterOperatorSet.All,
          BfxFindFilterConditionValueParseType.DateEquality,
        ),
        new BfxFindFilterCondition(
          5,
          4,
          BfxFindDealsFilterFieldName.FILL_USER,
          "Fill User",
          BfxFindFilterOperatorSet.EqualNotEqual,
          BfxFindFilterConditionValueParseType.RefArray,
        ),
        new BfxFindFilterCondition(
          5,
          5,
          BfxFindDealsFilterFieldName.VALUE_DATE,
          "Value Date",
          BfxFindFilterOperatorSet.All,
          BfxFindFilterConditionValueParseType.DateEquality,
        ),
        new BfxFindFilterCondition(
          5,
          6,
          BfxFindDealsFilterFieldName.TRADE_DATE,
          "Trade Date",
          BfxFindFilterOperatorSet.All,
          BfxFindFilterConditionValueParseType.DateEquality,
        ),
      ],
    };

    let searchByRate = {
      id: FilterData.templateId++,
      label: "Search by Rate",
      filterConditions: [
        new BfxFindFilterCondition(
          7,
          0,
          BfxFindDealsFilterFieldName.MARKET_SPOT,
          "Market Spot Rate",
          BfxFindFilterOperatorSet.All,
        ),
        new BfxFindFilterCondition(
          7,
          1,
          BfxFindDealsFilterFieldName.CLIENT_SPOT,
          "Client Spot Rate",
          BfxFindFilterOperatorSet.All,
        ),
      ],
    };
    if (clientTraderUsersEnabled) {
      searchByClient = {
        id: FilterData.templateId++,
        label: "Search by Client",
        filterConditions: [
          new BfxFindFilterCondition(
            1,
            0,
            BfxFindDealsFilterFieldName.CLIENT_ID,
            "Client ID",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            1,
            BfxFindDealsFilterFieldName.CLIENT_NAME,
            "Client Name",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            2,
            BfxFindDealsFilterFieldName.ACCOUNT_ID,
            "Account ID",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            3,
            BfxFindDealsFilterFieldName.ACCOUNT_NAME,
            "Account Name",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            4,
            BfxFindDealsFilterFieldName.CLIENT_TRADER,
            "Client Trader",
            BfxFindFilterOperatorSet.Equal,
          ),
          new BfxFindFilterCondition(
            1,
            5,
            BfxFindDealsFilterFieldName.BRANCH,
            "Branch",
            BfxFindFilterOperatorSet.InNotIn,
          ),
          new BfxFindFilterCondition(
            1,
            6,
            BfxFindDealsFilterFieldName.SALES_GROUP,
            "Sales Group",
            BfxFindFilterOperatorSet.EqualNotEqual,
          ),
          new BfxFindFilterCondition(
            1,
            7,
            BfxFindDealsFilterFieldName.CHANNEL,
            "Channel",
            BfxFindFilterOperatorSet.InNotIn,
          ),
        ],
      };

      if (this.oboEnabled) {
        searchByClient.filterConditions.push(
          new BfxFindFilterCondition(
            1,
            8,
            BfxFindDealsFilterFieldName.OBO_CLIENT,
            "OBO Client",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
          new BfxFindFilterCondition(
            1,
            9,
            BfxFindDealsFilterFieldName.OBO_ACCOUNT,
            "OBO Account",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
        );
      }
    } else {
      searchByClient = {
        id: FilterData.templateId++,
        label: "Search by Client",
        filterConditions: [
          new BfxFindFilterCondition(
            1,
            0,
            BfxFindDealsFilterFieldName.CLIENT_ID,
            "Client ID",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            1,
            BfxFindDealsFilterFieldName.CLIENT_NAME,
            "Client Name",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            2,
            BfxFindDealsFilterFieldName.ACCOUNT_ID,
            "Account ID",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            3,
            BfxFindDealsFilterFieldName.ACCOUNT_NAME,
            "Account Name",
            BfxFindFilterOperatorSet.In,
          ),
          new BfxFindFilterCondition(
            1,
            4,
            BfxFindDealsFilterFieldName.BRANCH,
            "Branch",
            BfxFindFilterOperatorSet.InNotIn,
          ),
          new BfxFindFilterCondition(
            1,
            5,
            BfxFindDealsFilterFieldName.SALES_GROUP,
            "Sales Group",
            BfxFindFilterOperatorSet.EqualNotEqual,
          ),
          new BfxFindFilterCondition(
            1,
            6,
            BfxFindDealsFilterFieldName.CHANNEL,
            "Channel",
            BfxFindFilterOperatorSet.InNotIn,
          ),
        ],
      };

      if (this.oboEnabled) {
        searchByClient.filterConditions.push(
          new BfxFindFilterCondition(
            1,
            7,
            BfxFindDealsFilterFieldName.OBO_CLIENT,
            "OBO Client",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
          new BfxFindFilterCondition(
            1,
            8,
            BfxFindDealsFilterFieldName.OBO_ACCOUNT,
            "OBO Account",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
        );
      }
    }
    if (ndfEnabled) {
      searchByDateAndUser = {
        id: FilterData.templateId++,
        label: "Search by Date or User",
        filterConditions: [
          new BfxFindFilterCondition(
            5,
            0,
            BfxFindDealsFilterFieldName.SEARCH_IN_TZ,
            "Search in",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.None,
          ),
          new BfxFindFilterCondition(
            5,
            1,
            BfxFindDealsFilterFieldName.ENTRY_DATE,
            "Entry Date",
            BfxFindFilterOperatorSet.All,
            BfxFindFilterConditionValueParseType.DateEquality,
          ),
          new BfxFindFilterCondition(
            5,
            2,
            BfxFindDealsFilterFieldName.ENTRY_USER,
            "Entry User",
            BfxFindFilterOperatorSet.EqualNotEqual,
            BfxFindFilterConditionValueParseType.RefArray,
          ),
          new BfxFindFilterCondition(
            5,
            3,
            BfxFindDealsFilterFieldName.FILL_DATE,
            "Fill Date",
            BfxFindFilterOperatorSet.All,
            BfxFindFilterConditionValueParseType.DateEquality,
          ),
          new BfxFindFilterCondition(
            5,
            4,
            BfxFindDealsFilterFieldName.FILL_USER,
            "Fill User",
            BfxFindFilterOperatorSet.EqualNotEqual,
            BfxFindFilterConditionValueParseType.RefArray,
          ),
          new BfxFindFilterCondition(
            5,
            5,
            BfxFindDealsFilterFieldName.VALUE_DATE,
            "Value Date",
            BfxFindFilterOperatorSet.All,
            BfxFindFilterConditionValueParseType.DateEquality,
          ),
          new BfxFindFilterCondition(
            5,
            6,
            BfxFindDealsFilterFieldName.TRADE_DATE,
            "Trade Date",
            BfxFindFilterOperatorSet.All,
            BfxFindFilterConditionValueParseType.DateEquality,
          ),
          new BfxFindFilterCondition(
            5,
            7,
            BfxFindDealsFilterFieldName.FIXING_TIME,
            "Fixing Time",
            BfxFindFilterOperatorSet.All,
            BfxFindFilterConditionValueParseType.None,
            true,
            true,
          ),
          new BfxFindFilterCondition(
            5,
            8,
            BfxFindDealsFilterFieldName.FIXING_DATE,
            "Fixing Date",
            BfxFindFilterOperatorSet.All,
            BfxFindFilterConditionValueParseType.DateEquality,
          ),
        ],
      };
    }
    if (fwdEnabled || ndfEnabled) {
      searchByRate.filterConditions = [
        new BfxFindFilterCondition(
          7,
          0,
          BfxFindDealsFilterFieldName.MARKET_SPOT,
          "Market Spot Rate",
          BfxFindFilterOperatorSet.All,
        ),
        new BfxFindFilterCondition(
          7,
          1,
          BfxFindDealsFilterFieldName.MARKET_ALL_IN,
          "Market All-In",
          BfxFindFilterOperatorSet.All,
        ),
        new BfxFindFilterCondition(
          7,
          2,
          BfxFindDealsFilterFieldName.CLIENT_SPOT,
          "Client Spot Rate",
          BfxFindFilterOperatorSet.All,
        ),
        new BfxFindFilterCondition(
          7,
          3,
          BfxFindDealsFilterFieldName.CLIENT_ALL_IN,
          "Client All-In",
          BfxFindFilterOperatorSet.All,
        ),
      ];
    }
    let data = [
      {
        id: FilterData.templateId++,
        label: "Search by Reference",
        filterConditions: [
          new BfxFindFilterCondition(
            0,
            0,
            BfxFindDealsFilterFieldName.ORDER_ID,
            "Order ID",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
          new BfxFindFilterCondition(
            0,
            1,
            BfxFindDealsFilterFieldName.TRADE_ID,
            "Trade ID",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.ExclusiveRefString,
          ),
          new BfxFindFilterCondition(
            0,
            2,
            BfxFindDealsFilterFieldName.STRATEGY_ID,
            "Strategy ID",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
          new BfxFindFilterCondition(
            0,
            3,
            BfxFindDealsFilterFieldName.EXTERNAL_REF,
            "External Reference",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.RefString,
          ),
          new BfxFindFilterCondition(
            0,
            4,
            BfxFindDealsFilterFieldName.BOOKING_UTI,
            "Booking UTI",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.ExclusiveRefString,
          ),
          new BfxFindFilterCondition(
            0,
            5,
            BfxFindDealsFilterFieldName.EXTERNAL_DEAL_ID,
            "Manual Deal Ref",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.ExclusiveRefString,
          ),
          new BfxFindFilterCondition(
            0,
            6,
            BfxFindDealsFilterFieldName.NETTING_ID,
            "Spot Portfolio",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.ExclusiveRefString,
          ),
          new BfxFindFilterCondition(
            0,
            7,
            BfxFindDealsFilterFieldName.FWD_NETTING_ID,
            "Forward Portfolio",
            BfxFindFilterOperatorSet.Equal,
            BfxFindFilterConditionValueParseType.ExclusiveRefString,
          ),
        ],
      },
      searchByClient,
      {
        id: FilterData.templateId++,
        label: "Search by Trader Group",
        filterConditions: [
          new BfxFindFilterCondition(
            2,
            0,
            BfxFindDealsFilterFieldName.TRADER_GROUP,
            "Trader Group",
            BfxFindFilterOperatorSet.EqualNotEqual,
          ),
        ],
      },
      {
        id: FilterData.templateId++,
        label: "Search by Instrument",
        filterConditions: [
          new BfxFindFilterCondition(
            3,
            0,
            BfxFindDealsFilterFieldName.CCY,
            "Ccy",
            BfxFindFilterOperatorSet.InNotIn,
          ),
          new BfxFindFilterCondition(
            3,
            1,
            BfxFindDealsFilterFieldName.INSTRUMENT,
            "Instrument",
            BfxFindFilterOperatorSet.InNotIn,
          ),
          new BfxFindFilterCondition(
            3,
            2,
            BfxFindDealsFilterFieldName.CCY_PAIR_GROUP,
            "Ccy Pair Group",
            BfxFindFilterOperatorSet.InNotIn,
            BfxFindFilterConditionValueParseType.InnerArray,
          ),
        ],
      },
      searchByType,
      searchByDateAndUser,
      {
        id: FilterData.templateId++,
        label: "Search by Amount",
        filterConditions: [
          new BfxFindFilterCondition(
            6,
            0,
            BfxFindDealsFilterFieldName.FILL_AMT,
            "Fill Amt",
            BfxFindFilterOperatorSet.All,
          ),
          new BfxFindFilterCondition(
            6,
            1,
            BfxFindDealsFilterFieldName.FILL_AMT_USD,
            "Fill Amt (USD)",
            BfxFindFilterOperatorSet.All,
          ),
        ],
      },
      searchByRate,
      {
        id: FilterData.templateId++,
        label: "Search by State",
        filterConditions: [
          new BfxFindFilterCondition(
            8,
            0,
            BfxFindDealsFilterFieldName.DEAL_STATE,
            "Deal State",
            BfxFindFilterOperatorSet.InNotIn,
          ),
        ],
      },
    ];

    // If we have active custom fields, add the search panel and fields to the view
    const custFieldData = this.getCustomFieldSearchData();
    if (custFieldData) {
      data.push(custFieldData);
    }

    const lastMarketSearchData = this.getLastMarketSearchData();
    if (lastMarketSearchData) {
      data.push(lastMarketSearchData);
    }

    this.defaultTimezoneField(data);

    return {
      data: data,
    };
  }

  protected getLastMarketSearchData(): any {
    return {
      id: FilterData.templateId++,
      label: "Search by Last Market",
      filterConditions: [
        new BfxFindFilterCondition(
          10,
          0,
          BfxFindDealsFilterFieldName.LAST_MARKET,
          "Last Market",
          BfxFindFilterOperatorSet.EqualNotEqual,
          BfxFindFilterConditionValueParseType.ExclusiveRefString,
        ),
      ],
    };
  }

  /**
   * Create all eligible filter conditions for the Find 'By Order Type' group
   * @param benchmarksEnabled
   * @param fwdEnabled
   * @param ndfEnabled
   * @returns
   */
  private createOrderTypeFilterConditions(
    benchmarksEnabled: boolean,
    fwdEnabled: boolean,
    ndfEnabled: boolean,
  ): BfxFindFilterCondition[] {
    const retValue: BfxFindFilterCondition[] = [];
    // consts so can fit constructors on single line...
    const inNotIn = BfxFindFilterOperatorSet.InNotIn;
    const fieldClass = BfxFindDealsFilterFieldName;

    let label = this.translate.instant("find.filter.label.type.order.type");
    let position: number = 0;
    retValue.push(new BfxFindFilterCondition(4, position++, fieldClass.TYPE, label, inNotIn));

    if (ndfEnabled || fwdEnabled || benchmarksEnabled) {
      label = this.translate.instant("find.filter.label.type.product");
      retValue.push(new BfxFindFilterCondition(4, position++, fieldClass.PRODUCT, label, inNotIn));
    }
    if (benchmarksEnabled) {
      label = this.translate.instant("find.filter.label.type.bm.fixing");
      retValue.push(
        new BfxFindFilterCondition(4, position++, fieldClass.BM_FIXCODE, label, inNotIn),
      );
    }
    if (ndfEnabled) {
      label = this.translate.instant("find.filter.label.type.ndf.fixing");
      retValue.push(new BfxFindFilterCondition(4, position++, fieldClass.FIXCODE, label, inNotIn));
    }
    if (benchmarksEnabled) {
      label = this.translate.instant("find.filter.label.type.trade.type");
      const condition = new BfxFindFilterCondition(
        4,
        position++,
        fieldClass.TRADE_TYPE,
        label,
        BfxFindFilterOperatorSet.Equal,
        BfxFindFilterConditionValueParseType.None,
      );
      retValue.push(condition);
    }

    return retValue;
  }
}
